import { composeClassNames } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import Heading from "src/components/Heading";
import Translate from "src/components/Translate";
import VideoListSlider from "src/components/VideoListSlider";
import constants from "src/constants";
import { mapContentToVideoCards } from "src/functions/mapContentToVideoCards";
import { sButton } from "src/styles/sButton.module.scss";
import { sGroupTitle } from "src/styles/sGroupTitle.module.scss";
import { sHomePageContent } from "src/styles/sHomePageContent.module.scss";

type Props = {
  series: any;
  classes: any;
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
};

function HomePageContent({ series, classes, style, className }: Props) {
  const { locale: lang, push } = useRouter();
  const [activeContent, setActiveContent] = useState<
    "serie" | "class" | "festival" | "stream" | "audio"
  >("serie");

  const [numberOfLectures, setNumberOfLectures] = useState(0);
  const [numberOfPrograms, setNumberOfPrograms] = useState(0);

  useEffect(() => {
    fetch(`${constants.Paths.apiUrl}/lectures/count`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        setNumberOfLectures(await res.json());
      }
    });
  }, []);

  useEffect(() => {
    fetch(`${constants.Paths.apiUrl}/programs/count`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        setNumberOfPrograms(await res.json());
      }
    });
  }, []);

  return (
    <div
      style={style}
      className={composeClassNames([
        sHomePageContent,
        className,
        "homepage-content-wrapper",
      ])}
    >
      <Heading
        type={`h2`}
        title={<Translate tKey={`Inspiring Content`} />}
        className={composeClassNames([sGroupTitle, "homepage-content-title"])}
      />
      <p className={"homepage-content-subtitle"}>
        <Translate
          tKey={`See what series, classes and festivals other users have been interested in.`}
        />
      </p>
      <div className="homepage-content-switch-wrapper">
        <div className="homepage-content-switch">
          <Button
            variant={`outline-primary`}
            size={`sm`}
            className={composeClassNames([
              sButton,
              `switch-button`,
              activeContent === "serie" ? "active" : null,
            ])}
            onClick={() => setActiveContent("serie")}
          >
            <Translate tKey={`Series`} />
          </Button>
          <Button
            variant={`outline-primary`}
            size={`sm`}
            className={composeClassNames([
              sButton,
              `switch-button`,
              activeContent === "festival" ? "active" : null,
            ])}
            onClick={() => setActiveContent("festival")}
          >
            <Translate tKey={`Festivals`} />
          </Button>
          <Button
            variant={`outline-primary`}
            size={`sm`}
            className={composeClassNames([
              sButton,
              `switch-button`,
              activeContent === "class" ? "active" : null,
            ])}
            onClick={() => setActiveContent("class")}
          >
            <Translate tKey={`Classes`} />
          </Button>
          {constants.Show.homepageContentStream && (
            <Button
              variant={`outline-primary`}
              size={`sm`}
              className={composeClassNames([
                sButton,
                `switch-button`,
                activeContent === "stream" ? "active" : null,
              ])}
              onClick={() => setActiveContent("stream")}
            >
              <Translate tKey={`Streams`} />
            </Button>
          )}
          {constants.Show.homepageContentAudio && (
            <Button
              variant={`outline-primary`}
              size={`sm`}
              className={composeClassNames([
                sButton,
                `switch-button`,
                activeContent === "audio" ? "active" : null,
              ])}
              onClick={() => setActiveContent("audio")}
            >
              <Translate tKey={`Audio`} />
            </Button>
          )}
        </div>
      </div>
      {activeContent === "serie" && series && (
        <VideoListSlider
          data={mapContentToVideoCards(series)}
          contentType={`serie`}
          type={`preview`}
          size={`lg`}
        />
      )}
      {activeContent === "festival" && series && (
        <VideoListSlider
          data={mapContentToVideoCards(series)}
          contentType={`festival`}
          type={`preview`}
          size={`lg`}
        />
      )}
      {activeContent === "class" && classes && (
        <VideoListSlider
          data={mapContentToVideoCards(classes)}
          contentType={`class`}
          type={`preview`}
          size={`lg`}
        />
      )}
      {constants.Show.homepageContentStream &&
        activeContent === "stream" &&
        classes && (
          <VideoListSlider
            data={mapContentToVideoCards(classes)}
            contentType={`stream`}
            type={`preview`}
            size={`lg`}
          />
        )}
      {constants.Show.homepageContentAudio &&
        activeContent === "audio" &&
        classes && (
          <VideoListSlider
            data={mapContentToVideoCards(classes)}
            contentType={`audio`}
            type={`preview`}
            size={`lg`}
          />
        )}
      <div className={"show-all"}>
        <Button
          onClick={() => push("/classes?paid=true")}
          className={composeClassNames([sButton])}
        >
          <Translate tKey={`Explore inspiring content`} /> (
          {numberOfLectures + numberOfPrograms} <Translate tKey={`items`} />)
        </Button>
      </div>
    </div>
  );
}

export default HomePageContent;
